import {Suspense, useCallback, useEffect, useRef, useState} from 'react'
import {announceFromElement} from '@github-ui/aria-live'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {type TimelineSort, useTimelineSort} from '../../../contexts/TimelineSortContext'
import {TimelinePanelContent} from './TimelinePanelContent'
import {TimelinePanelSkeleton} from './TimelinePanelSkeleton'
import styles from './TimelinePanel.module.css'

function TimelinePanelError({onShow}: {onShow?: () => void}) {
  const ref = useRef<HTMLDivElement>(null)
  // Focus stays on the control that triggered the load, so the failure has to be announced.
  useEffect(() => {
    if (ref.current) announceFromElement(ref.current)
  }, [])
  useEffect(() => {
    onShow?.()
  }, [onShow])
  return (
    <div ref={ref} className={styles.emptyState}>
      Unable to load activity.
    </div>
  )
}

export function TimelinePanel() {
  const sort = useTimelineSort()
  const [failedSort, setFailedSort] = useState<TimelineSort | null>(null)
  const [recoveries, setRecoveries] = useState(0)
  const markFailed = useCallback(() => setFailedSort(sort), [sort])
  // Picking another order after a failed load remounts the inner boundary to clear the error. A normal sort change
  // keeps the list mounted so its result announcements still run, and Suspense keeps it up while the next order loads.
  if (failedSort !== null && failedSort !== sort) {
    setFailedSort(null)
    setRecoveries(count => count + 1)
  }
  return (
    <ErrorBoundary boundaryName="TimelinePanelShell" fallback={<TimelinePanelError />}>
      <Suspense fallback={<TimelinePanelSkeleton count={25} />}>
        <ErrorBoundary
          key={recoveries}
          boundaryName="TimelinePanel"
          fallback={<TimelinePanelError onShow={markFailed} />}
        >
          <TimelinePanelContent />
        </ErrorBoundary>
      </Suspense>
    </ErrorBoundary>
  )
}
